// apps/web/src/pages/admin/AdminDashboard.tsx
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAuth } from '../../hooks/useAuth';
import {
  CheckCircle,
  XCircle,
  Eye,
  Download,
  Loader2,
  Users,
  Clock,
  AlertCircle,
  Search,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Pencil,
} from 'lucide-react';

type AlumniRow = {
  id: string;
  name: string;
  department?: string | null;
  graduateYear?: number | null;
  currentCompany?: string | null;
  currentTitle?: string | null;
  avatarUrl?: string | null;
  cardImageUrl?: string | null;
  isPublic?: boolean;
  isVerified?: boolean;
};

export default function AdminDashboard() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isOwner = !!user?.isOwner;
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  // 預設「全部」：已審核的自己也會出現
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'verified'>('all');
  const [confirmDelete, setConfirmDelete] = useState<AlumniRow | null>(null);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['admin-alumni', page, search, statusFilter],
    queryFn: () =>
      api.admin.listAlumni({
        page,
        status: statusFilter,
        q: search || undefined,
        limit: 20,
      }),
  });

  const verifyMutation = useMutation({
    mutationFn: ({
      id,
      verified,
      setPrivate,
    }: {
      id: string;
      verified: boolean;
      setPrivate?: boolean;
    }) => api.admin.verify(id, verified, { setPrivate }),
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['admin-alumni'] });
      toast.success(vars.verified ? '已通過審核' : '已撤回公開審核');
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : '操作失敗'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.admin.deleteAlumni(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin-alumni'] });
      setConfirmDelete(null);
      toast.success(`已刪除「${res.name || '該校友'}」的申請與資料`);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : '刪除失敗'),
  });

  const exportMutation = useMutation({
    mutationFn: () => api.admin.exportCsv(),
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `alumni-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    },
  });

  const busyId =
    (verifyMutation.isPending && verifyMutation.variables?.id) ||
    (deleteMutation.isPending && deleteMutation.variables) ||
    null;

  if (isLoading) return <LoadingSpinner size="lg" className="mx-auto my-16" />;

  if (error) {
    return (
      <div className="container-custom py-12 text-center text-red-600">
        載入失敗：{error instanceof Error ? error.message : '請重新登入後再試'}
      </div>
    );
  }

  const items = (data?.items ?? []) as AlumniRow[];
  const total = data?.total ?? 0;
  const stats = data?.stats ?? {
    total: 0,
    pending: 0,
    verified: 0,
  };

  return (
    <div className="container-custom">
      <div className="mb-8 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">管理員儀表板</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">審核校友名片、管理公開名冊</p>
          </div>
          {isOwner && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => exportMutation.mutate()}
                disabled={exportMutation.isPending}
                className="btn-secondary"
              >
                <Download className="w-4 h-4 mr-1" />
                匯出完整名冊 CSV
              </button>
              <Link to="/admin/users" className="btn-outline">
                <Users className="w-4 h-4 mr-1" />
                會員管理
              </Link>
            </div>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-3 mt-6">
          <StatCard title="待審核" value={stats.pending} icon={Clock} color="yellow" />
          <StatCard title="已通過" value={stats.verified} icon={CheckCircle} color="green" />
          <StatCard title="總計" value={stats.total} icon={Users} color="blue" />
        </div>
      </div>

      <div className="card mb-6 animate-slide-up">
        <div className="p-4 sm:p-6">
          <form
            className="flex flex-col sm:flex-row gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              setSearch(searchInput.trim());
              setPage(1);
            }}
          >
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="搜尋姓名、公司、職稱、科系..."
                className="input pl-10"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as 'all' | 'pending' | 'verified');
                setPage(1);
              }}
              className="input w-auto sm:w-40"
            >
              <option value="all">全部狀態</option>
              <option value="pending">待審核</option>
              <option value="verified">已通過</option>
            </select>
            <button type="submit" className="btn-primary whitespace-nowrap">
              搜尋
            </button>
          </form>
          {isFetching && (
            <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> 更新中…
            </p>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="card text-center py-16 animate-fade-in">
          <AlertCircle className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">暫無資料</h3>
          <p className="text-gray-500 dark:text-gray-400">
            {statusFilter === 'pending'
              ? '目前沒有待審核的校友（已通過者請改選「全部」或「已通過」）'
              : '目前沒有符合條件的校友資料'}
          </p>
        </div>
      ) : (
        <>
          {/* 手機：卡片列表（避免表格欄位過窄導致姓名逐字直排） */}
          <div className="md:hidden space-y-3 animate-slide-up">
            {items.map((item) => (
              <AlumniMobileCard
                key={item.id}
                item={item}
                isSelf={user?.id === item.id}
                canEditOthers={isOwner}
                busy={busyId === item.id}
                verifyPending={verifyMutation.isPending && busyId === item.id}
                onVerify={(verified, setPrivate) =>
                  verifyMutation.mutate({ id: item.id, verified, setPrivate })
                }
                onDelete={() => setConfirmDelete(item)}
              />
            ))}
          </div>

          {/* 桌機：表格 */}
          <div className="hidden md:block card overflow-hidden animate-slide-up">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      姓名 / 科系
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      公司 / 職稱
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                      畢業年份
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      狀態
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {items.map((item) => (
                    <tr
                      key={item.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                    >
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <AvatarCircle item={item} />
                          <div className="min-w-0">
                            <Link
                              to={`/directory/${item.id}`}
                              className="font-medium text-gray-900 dark:text-white hover:text-alumni-600 block truncate"
                            >
                              {item.name}
                            </Link>
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                              {item.department || '未填寫科系'}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 max-w-[12rem]">
                        <p className="text-sm text-gray-700 dark:text-gray-300 truncate">
                          {item.currentCompany || '未填寫'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {item.currentTitle || '未填寫職稱'}
                        </p>
                      </td>
                      <td className="px-4 py-4 hidden lg:table-cell text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {item.graduateYear != null
                          ? `西元 ${item.graduateYear}／民國 ${item.graduateYear - 1911}`
                          : '未填寫'}
                      </td>
                      <td className="px-4 py-4">
                        <StatusBadges item={item} />
                      </td>
                      <td className="px-4 py-4 text-right whitespace-nowrap">
                        <ActionButtons
                          item={item}
                          isSelf={user?.id === item.id}
                          canEditOthers={isOwner}
                          busy={busyId === item.id}
                          verifyPending={verifyMutation.isPending && busyId === item.id}
                          onVerify={(verified, setPrivate) =>
                            verifyMutation.mutate({ id: item.id, verified, setPrivate })
                          }
                          onDelete={() => setConfirmDelete(item)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {total > 20 && (
            <div className="mt-4 px-1 py-3 flex items-center justify-between">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                顯示第 {(page - 1) * 20 + 1} - {Math.min(page * 20, total)} 筆，共 {total} 筆
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn-ghost btn-sm"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page * 20 >= total}
                  className="btn-ghost btn-sm"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* 駁回／刪除確認 */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-dialog-title"
          onClick={() => !deleteMutation.isPending && setConfirmDelete(null)}
        >
          <div
            className="card max-w-md w-full p-6 shadow-xl animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/40">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2
                  id="delete-dialog-title"
                  className="text-lg font-bold text-gray-900 dark:text-white"
                >
                  {confirmDelete.isVerified ? '刪除校友資料' : '駁回並刪除申請'}
                </h2>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  確定要
                  {confirmDelete.isVerified ? '刪除' : '駁回並刪除'}
                  <strong className="text-gray-900 dark:text-white mx-1">
                    {confirmDelete.name || '此校友'}
                  </strong>
                  的全部資料嗎？
                  <br />
                  包含名片圖、個人資料與帳號紀錄，
                  <span className="text-red-600 font-medium">無法復原</span>
                  。對方之後可用 LINE 重新登入再申請。
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="btn-secondary"
                disabled={deleteMutation.isPending}
                onClick={() => setConfirmDelete(null)}
              >
                取消
              </button>
              <button
                type="button"
                className="btn bg-red-600 text-white hover:bg-red-700 px-4 py-2"
                disabled={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate(confirmDelete.id)}
              >
                {deleteMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                    刪除中…
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-1" />
                    確定刪除
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AvatarCircle({ item }: { item: AlumniRow }) {
  return item.avatarUrl ? (
    <img
      src={item.avatarUrl}
      alt=""
      className="w-11 h-11 rounded-full object-cover shrink-0"
      referrerPolicy="no-referrer"
    />
  ) : (
    <div className="w-11 h-11 rounded-full bg-alumni-100 flex items-center justify-center shrink-0">
      <span className="text-alumni-600 font-bold">{(item.name || '?').charAt(0)}</span>
    </div>
  );
}

function StatusBadges({ item }: { item: AlumniRow }) {
  return (
    <div className="flex flex-col gap-1 items-start">
      {item.isVerified ? (
        <span className="badge badge-green inline-flex items-center gap-1 w-fit whitespace-nowrap">
          <CheckCircle className="w-3 h-3 shrink-0" />
          已通過
        </span>
      ) : (
        <span className="badge badge-yellow inline-flex items-center gap-1 w-fit whitespace-nowrap">
          <Clock className="w-3 h-3 shrink-0" />
          待審核
        </span>
      )}
      <span className="text-xs text-gray-400 whitespace-nowrap">
        {item.isPublic ? '公開' : '不公開'}
      </span>
    </div>
  );
}

function ActionButtons({
  item,
  isSelf,
  canEditOthers,
  busy,
  verifyPending,
  onVerify,
  onDelete,
  stacked = false,
}: {
  item: AlumniRow;
  isSelf: boolean;
  /** 主管理者可代編他人 */
  canEditOthers: boolean;
  busy: boolean;
  verifyPending: boolean;
  onVerify: (verified: boolean, setPrivate?: boolean) => void;
  onDelete: () => void;
  stacked?: boolean;
}) {
  const showEdit = isSelf || canEditOthers;
  return (
    <div
      className={
        stacked
          ? 'flex flex-wrap items-center gap-2'
          : 'inline-flex items-center justify-end gap-1.5 flex-nowrap'
      }
    >
      <Link
        to={`/directory/${item.id}`}
        className="btn-ghost btn-sm !inline-flex !items-center !gap-1 p-2 shrink-0"
        title="檢視公開頁面"
      >
        <Eye className="w-4 h-4 shrink-0" />
        {stacked && <span>檢視</span>}
      </Link>
      {showEdit && (
        <Link
          to={isSelf ? '/profile' : `/profile/edit/${item.id}`}
          className="btn-ghost btn-sm !inline-flex !items-center !gap-1 p-2 shrink-0 text-alumni-600"
          title={isSelf ? '編輯我的名片' : '代編此校友名片'}
        >
          <Pencil className="w-4 h-4 shrink-0" />
          {stacked && <span>編輯</span>}
        </Link>
      )}
      {item.isVerified ? (
        <button
          type="button"
          onClick={() => onVerify(false, true)}
          disabled={busy}
          className="btn-ghost btn-sm !inline-flex !items-center !gap-1 px-2.5 py-1.5 shrink-0"
          title="撤回通過（改回待審且不公開）"
        >
          {verifyPending ? (
            <Loader2 className="w-4 h-4 animate-spin shrink-0" />
          ) : (
            <>
              <XCircle className="w-4 h-4 shrink-0" />
              <span>撤回</span>
            </>
          )}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onVerify(true)}
          disabled={busy}
          className="btn-primary btn-sm !inline-flex !items-center !gap-1 px-2.5 py-1.5 shrink-0"
          title="審核通過"
        >
          {verifyPending ? (
            <Loader2 className="w-4 h-4 animate-spin shrink-0" />
          ) : (
            <>
              <CheckCircle className="w-4 h-4 shrink-0" />
              <span>通過</span>
            </>
          )}
        </button>
      )}
      {!isSelf && (
        <button
          type="button"
          onClick={onDelete}
          disabled={busy}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg shrink-0 whitespace-nowrap text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 border border-red-200 dark:border-red-800 disabled:opacity-50"
          title={item.isVerified ? '刪除此校友資料' : '駁回並刪除申請'}
        >
          <Trash2 className="w-4 h-4 shrink-0" />
          <span>{item.isVerified ? '刪除' : '駁回刪除'}</span>
        </button>
      )}
    </div>
  );
}

/** 手機專用：橫向資訊卡，姓名／科系橫排不換字 */
function AlumniMobileCard({
  item,
  isSelf,
  canEditOthers,
  busy,
  verifyPending,
  onVerify,
  onDelete,
}: {
  item: AlumniRow;
  isSelf: boolean;
  canEditOthers: boolean;
  busy: boolean;
  verifyPending: boolean;
  onVerify: (verified: boolean, setPrivate?: boolean) => void;
  onDelete: () => void;
}) {
  return (
    <article className="card p-4">
      <div className="flex items-start gap-3">
        <AvatarCircle item={item} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <Link
                to={`/directory/${item.id}`}
                className="font-semibold text-base text-gray-900 dark:text-white hover:text-alumni-600 block break-words"
              >
                {item.name || '（未填姓名）'}
              </Link>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 break-words">
                {item.department || '未填寫科系'}
              </p>
              {(item.currentCompany || item.currentTitle) && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 break-words">
                  {[item.currentCompany, item.currentTitle].filter(Boolean).join(' · ')}
                </p>
              )}
              {item.graduateYear != null && (
                <p className="text-xs text-gray-400 mt-1">
                  西元 {item.graduateYear}／民國 {item.graduateYear - 1911}
                </p>
              )}
            </div>
            <StatusBadges item={item} />
          </div>
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            <ActionButtons
              item={item}
              isSelf={isSelf}
              canEditOthers={canEditOthers}
              busy={busy}
              verifyPending={verifyPending}
              onVerify={onVerify}
              onDelete={onDelete}
              stacked
            />
          </div>
        </div>
      </div>
    </article>
  );
}

function StatCard({
  title,
  value,
  icon: Icon,
  color,
}: {
  title: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  color: 'yellow' | 'green' | 'blue';
}) {
  const colorClasses = {
    yellow:
      'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800',
    green:
      'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800',
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800',
  };

  return (
    <div className={`card p-4 border ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-80">{title}</p>
          <p className="text-2xl font-bold">{Number(value || 0).toLocaleString()}</p>
        </div>
        <Icon className="w-8 h-8 opacity-50" />
      </div>
    </div>
  );
}
